/**
 * Admin Portal: Streaming control — show/hide streaming services on the Streaming page.
 * Only displayed services appear as filter cards. Same pattern as Channel control.
 */
import { useAuth } from '@/contexts/AuthContext'
import { useAdmin } from '@/hooks/useAdmin'
import { Navigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { STREAMING_SERVICE_DISPLAY_QUERY_KEY } from '@/hooks/useStreamingServiceDisplay'

const STREAMING_SERVICE_LIST_QUERY_KEY = ['streaming-service-display-list'] as const

/** Service IDs and display info for the admin list. Must match streaming_service_display.service_id and Streaming UI. */
const STREAMING_SERVICES: { service_id: string; name: string; logoUrl: string }[] = [
  { service_id: 'Amazon', name: 'Amazon', logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/c/ca/Amazon_Prime_Video_logo_%282024%29.svg' },
  { service_id: 'Netflix', name: 'Netflix', logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/7/75/Netflix_icon.svg' },
  { service_id: 'Hulu', name: 'Hulu', logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/f/f9/Hulu_logo_%282018%29.svg' },
  { service_id: 'Apple TV', name: 'Apple TV', logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/a/ad/AppleTVLogo.svg' },
  { service_id: 'Paramount Plus', name: 'Paramount Plus', logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/a/a5/Paramount_Plus.svg' },
]

interface StreamingServiceDisplayRow {
  service_id: string
  is_displayed: boolean
  updated_at: string
}

export default function AdminStreamingControlPage() {
  const { user } = useAuth()
  const isAdmin = useAdmin()
  const queryClient = useQueryClient()

  const { data: displayRows = [], isLoading: listLoading, error: listError } = useQuery({
    queryKey: STREAMING_SERVICE_LIST_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('streaming_service_display')
        .select('service_id, is_displayed, updated_at')
      if (error) throw error
      return (data || []) as StreamingServiceDisplayRow[]
    },
    enabled: !!user && isAdmin,
  })

  const updateDisplayMutation = useMutation({
    mutationFn: async ({ service_id, is_displayed }: { service_id: string; is_displayed: boolean }) => {
      const { error } = await supabase
        .from('streaming_service_display')
        .update({ is_displayed })
        .eq('service_id', service_id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STREAMING_SERVICE_LIST_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: STREAMING_SERVICE_DISPLAY_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['streaming-listings'] })
    },
  })

  const bulkUpdateMutation = useMutation({
    mutationFn: async ({ is_displayed, service_ids }: { is_displayed: boolean; service_ids: string[] }) => {
      const { error } = await supabase
        .from('streaming_service_display')
        .update({ is_displayed })
        .in('service_id', service_ids)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STREAMING_SERVICE_LIST_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: STREAMING_SERVICE_DISPLAY_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['streaming-listings'] })
    },
  })

  const displayByServiceId = new Map(displayRows.map((r) => [r.service_id, r.is_displayed]))
  const mergedList = STREAMING_SERVICES.map((s) => ({
    ...s,
    is_displayed: displayByServiceId.get(s.service_id) ?? true,
  }))

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1
        className="text-3xl font-bold mb-6"
        style={{ color: 'var(--text-primary)' }}
      >
        Streaming control
      </h1>

      <section
        className="rounded-lg p-6 border-2"
        style={{ borderColor: 'var(--bg-tertiary)', backgroundColor: 'var(--bg-secondary)' }}
        aria-labelledby="streaming-control-heading"
      >
        <h2 id="streaming-control-heading" className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
          Show or hide streaming services on the Streaming page
        </h2>
        <p className="mb-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
          Only selected services appear as filter cards on the Streaming page. Toggle each service to show or hide it.
        </p>
        {listLoading ? (
          <p style={{ color: 'var(--text-secondary)' }}>Loading…</p>
        ) : listError ? (
          <p style={{ color: 'var(--color-error)' }} role="alert">
            {listError instanceof Error ? listError.message : 'Failed to load streaming services.'}
          </p>
        ) : (
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <button
                type="button"
                onClick={() => bulkUpdateMutation.mutate({ is_displayed: true, service_ids: STREAMING_SERVICES.map((s) => s.service_id) })}
                disabled={bulkUpdateMutation.isPending || updateDisplayMutation.isPending}
                className="px-3 py-1.5 text-sm font-medium rounded border"
                style={{
                  borderColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--bg-tertiary)',
                }}
              >
                {bulkUpdateMutation.isPending ? 'Updating…' : 'Select all'}
              </button>
              <button
                type="button"
                onClick={() => bulkUpdateMutation.mutate({ is_displayed: false, service_ids: STREAMING_SERVICES.map((s) => s.service_id) })}
                disabled={bulkUpdateMutation.isPending || updateDisplayMutation.isPending}
                className="px-3 py-1.5 text-sm font-medium rounded border"
                style={{
                  borderColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--bg-tertiary)',
                }}
              >
                {bulkUpdateMutation.isPending ? 'Updating…' : 'Deselect all'}
              </button>
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                Set all services to shown or hidden on the Streaming page.
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ color: 'var(--text-primary)' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--bg-tertiary)' }}>
                    <th className="text-left py-2 pr-4">Service</th>
                    <th className="text-left py-2 pr-4">Show on Streaming page</th>
                  </tr>
                </thead>
                <tbody>
                  {mergedList.map((row) => (
                    <tr key={row.service_id} style={{ borderBottom: '1px solid var(--bg-tertiary)' }}>
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-2">
                          <img
                            src={row.logoUrl}
                            alt=""
                            className="h-8 w-auto max-w-[120px] object-contain"
                            referrerPolicy="no-referrer"
                          />
                          <span>{row.name}</span>
                        </div>
                      </td>
                      <td className="py-2 pr-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={row.is_displayed}
                            onChange={(e) => updateDisplayMutation.mutate({ service_id: row.service_id, is_displayed: e.target.checked })}
                            disabled={updateDisplayMutation.isPending || bulkUpdateMutation.isPending}
                            className="rounded"
                            aria-label={`Show ${row.name} on Streaming page`}
                          />
                          <span style={{ color: 'var(--text-secondary)' }}>{row.is_displayed ? 'Shown' : 'Hidden'}</span>
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
